import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import CurrencyInput from "../components/CurrencyInput";

const PAGE_SIZE = 25; // buildings per page

export default function SponsorDealsPage() {
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [totalBuildings, setTotalBuildings] = useState(0);
  const [totalUnits, setTotalUnits] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);

  useEffect(() => {
    authFetch("/sale-reviews/neighborhoods")
      .then((data) => setNeighborhoods(data.neighborhoods || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, propertyType, priceMin, priceMax, includeClosed, selectedNeighborhoods]);

  const loadBuildings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (propertyType) params.append("property_type", propertyType);
      if (priceMin) params.append("price_min", priceMin);
      if (priceMax) params.append("price_max", priceMax);
      if (includeClosed) params.append("include_closed", "true");
      if (selectedNeighborhoods.length > 0) params.append("neighborhood_ids", selectedNeighborhoods.join(","));
      const data = await authFetch(`/listings/sponsor-deals?${params}`);
      setBuildings(data.buildings || []);
      setTotalBuildings(data.total_buildings || 0);
      setTotalUnits(data.total_units || 0);
      setPage(1);
    } catch (e) {
      console.error("Error loading sponsor deals:", e);
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => { setSearch(searchInput.trim()); };
  const resetFilters = () => {
    setSearchInput(""); setSearch(""); setPropertyType("");
    setPriceMin(""); setPriceMax(""); setIncludeClosed(false);
    setSelectedNeighborhoods([]);
  };
  const onFilter = (setter) => (e) => setter(e.target.value);

  const totalPages = Math.max(1, Math.ceil(totalBuildings / PAGE_SIZE));
  const pageBuildings = buildings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const fmtPrice = (v) => (v != null ? `$${Math.round(v).toLocaleString()}` : "—");
  const priceRange = (b) =>
    b.price_min == null ? "—"
      : b.price_min === b.price_max ? fmtPrice(b.price_min)
      : `${fmtPrice(b.price_min)} – ${fmtPrice(b.price_max)}`;
  const ownLabel = (o) => (o === "COOP" ? "Co-op" : o === "CONDP" ? "Condop" : o === "CONDO" ? "Condo" : o || "—");

  const inputStyle = { width: "100%", padding: "6px 8px", border: "1px solid #ced4da", borderRadius: "4px", fontSize: "13px", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" };
  const headTh = { textAlign: "left", padding: "8px 10px", fontSize: "11px", fontWeight: 700, color: "#555", borderBottom: "2px solid #e0e0e0", textTransform: "uppercase", whiteSpace: "nowrap" };
  const td = { padding: "8px 10px", fontSize: "13px", color: "#333", borderBottom: "1px solid #eee", verticalAlign: "middle" };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />
      <div style={{ padding: "20px", maxWidth: "1700px", margin: "0 auto" }}>
        <h1 style={{ fontSize: "22px", fontWeight: 700, margin: "0 0 4px 0", color: "#222" }}>Sponsor Deals</h1>
        <p style={{ fontSize: "13px", color: "#777", margin: "0 0 16px 0" }}>
          Sponsor units (no board approval), grouped by building.
        </p>

        {/* Filters */}
        <div style={{ padding: "16px", backgroundColor: "#fff", borderRadius: "8px", marginBottom: "16px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px" }}>
            <NeighborhoodSelect
              neighborhoods={neighborhoods}
              selected={selectedNeighborhoods}
              onChange={(v) => setSelectedNeighborhoods(v)}
              placeholder="All Neighborhoods"
              gridColumn="span 2"
            />
            <div>
              <label style={labelStyle}>Address</label>
              <input type="text" placeholder="Search…" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Ownership</label>
              <select value={propertyType} onChange={onFilter(setPropertyType)} style={{ ...inputStyle, backgroundColor: "#fff" }}>
                <option value="">All</option>
                <option value="condo">Condo</option>
                <option value="coop">Co-op / Condop</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Min Price</label>
              <CurrencyInput placeholder="0" value={priceMin}
                onChange={setPriceMin} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Max Price</label>
              <CurrencyInput placeholder="Any" value={priceMax}
                onChange={setPriceMax} style={inputStyle} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#495057", cursor: "pointer" }}>
                <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} />
                Include closed/sold
              </label>
            </div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "8px" }}>
              <button onClick={applySearch} disabled={loading}
                style={{ padding: "8px 18px", backgroundColor: "#000", color: "#fff", border: "none", borderRadius: "4px", fontSize: "14px", fontWeight: 500, cursor: loading ? "wait" : "pointer" }}>
                {loading ? "…" : "Search"}
              </button>
              <button onClick={resetFilters}
                style={{ padding: "8px 14px", backgroundColor: "#6c757d", color: "#fff", border: "none", borderRadius: "4px", fontSize: "14px", fontWeight: 500, cursor: "pointer" }}>
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Count + pagination */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <div style={{ fontSize: "13px", color: "#666", fontWeight: 500 }}>
            {totalBuildings > 0
              ? `${totalBuildings.toLocaleString()} buildings · ${totalUnits.toLocaleString()} active sponsor units`
              : "No results"}
          </div>
          <Pager page={page} totalPages={totalPages} loading={loading} setPage={setPage} />
        </div>

        {/* Results */}
        <div style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)", overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={headTh}>Building</th>
                <th style={headTh}>Ownership</th>
                <th style={headTh}>Neighborhood</th>
                <th style={{ ...headTh, textAlign: "center" }}>Bldg Units</th>
                <th style={{ ...headTh, textAlign: "center" }}>Sponsor Units</th>
                <th style={{ ...headTh, textAlign: "center" }} title="Sponsor units sold in the last 12 months">Sold (12mo)</th>
                <th style={headTh}>Price Range</th>
                <th style={{ ...headTh, textAlign: "center" }}>Best Cap</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ ...td, textAlign: "center", padding: 30, color: "#999" }}>Loading…</td></tr>
              ) : pageBuildings.length === 0 ? (
                <tr><td colSpan={8} style={{ ...td, textAlign: "center", padding: 30, color: "#999" }}>No sponsor units match these filters.</td></tr>
              ) : (
                pageBuildings.map((b) => (
                  <tr key={b.property_id}
                    onClick={() => navigate(`/sponsor-deals/building/${b.property_id}`)}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f9ff")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                    <td style={{ ...td, color: "#222" }}>
                      <div style={{ fontWeight: 600 }}>{b.address}</div>
                      {b.building_name && <div style={{ fontSize: 11, color: "#8a8a8a" }}>{b.building_name}</div>}
                    </td>
                    <td style={td}>{ownLabel(b.ownership_type)}</td>
                    <td style={td}>{b.neighborhood || "—"}</td>
                    <td style={{ ...td, textAlign: "center", color: "#555" }}>{b.total_units ?? "—"}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      <span style={{ fontWeight: 700, color: "#1565c0", background: "#e3f0ff", borderRadius: 10, padding: "1px 9px" }}>{b.active_count} active</span>
                      {b.sold_count > 0 && <span style={{ fontSize: 11, color: "#999", marginLeft: 6 }}>· {b.sold_count} sold</span>}
                      {b.pending_count > 0 && <span style={{ fontSize: 11, color: "#8a6d3b", marginLeft: 6 }}>· {b.pending_count} in contract</span>}
                    </td>
                    <td style={{ ...td, textAlign: "center", color: "#555", fontWeight: 600 }}>
                      {b.sold_12mo || 0}
                    </td>
                    <td style={{ ...td, fontWeight: 600 }}>{priceRange(b)}</td>
                    <td style={{ ...td, textAlign: "center" }}>{b.best_net_yield != null ? `${b.best_net_yield.toFixed(1)}%` : <span style={{ color: "#bbb" }}>—</span>}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
          <Pager page={page} totalPages={totalPages} loading={loading} setPage={setPage} />
        </div>
      </div>
    </div>
  );
}

function Pager({ page, totalPages, loading, setPage }) {
  const btn = (disabled) => ({ padding: "5px 12px", fontSize: "13px", border: "1px solid #ccc", borderRadius: "4px", background: disabled ? "#f5f5f5" : "#fff", color: disabled ? "#aaa" : "#333", cursor: disabled ? "not-allowed" : "pointer" });
  const atStart = page <= 1, atEnd = page >= totalPages;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <button style={btn(atStart || loading)} disabled={atStart || loading} onClick={() => setPage(page - 1)}>‹ Prev</button>
      <span style={{ fontSize: "13px", color: "#666" }}>Page {page} of {totalPages}</span>
      <button style={btn(atEnd || loading)} disabled={atEnd || loading} onClick={() => setPage(page + 1)}>Next ›</button>
    </div>
  );
}
